import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import BetaInviteCard from './BetaInviteCard';
import { useAuth } from './AuthContext';
import { shortDisplayTitle } from './displayText';
import { getSavedLooks, isLookSaved, toggleSavedLook } from './savedLookStore';
import useModalEscape from './useModalEscape';

const fallbackLooks = [
  'https://images.unsplash.com/photo-1496747611176-843222e1e57c?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=crop&w=1200&q=80',
];

const introDemoFit = {
  shareId: 'v9Ik1TlUYzdPocbFMej0HnJT',
  eventId: 277,
  sourceImageUrl: 'https://cdn.outfitdetective.com/outfit-detective/instagram/catalinbotezatu1/2026/05/3888812657615281777/cover.jpg',
  resultImageUrl: 'https://cdn.outfitdetective.com/outfit-detective/users/5/try-on/c2812852-c0d4-492b-b85b-a33d9ed3b039.webp',
};

const FEED_PAGE_LIMIT = 10;
const FEED_SCROLL_KEY = 'od_feed_scroll_y';
const FEED_CACHE_KEY = 'od_feed_cache_v2';
const FEED_CACHE_TTL_MS = 60 * 1000;

function readFeedRandomSeed() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function feedCacheKey(language) {
  return `${FEED_CACHE_KEY}_${String(language || 'en')}`;
}

function readFeedCache(language) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(feedCacheKey(language)) || 'null');
    if (!cached?.savedAt || Date.now() - Number(cached.savedAt) > FEED_CACHE_TTL_MS) return null;
    if (!Array.isArray(cached.feed)) return null;
    return cached;
  } catch {
    return null;
  }
}

function writeFeedCache(language, payload) {
  try {
    sessionStorage.setItem(feedCacheKey(language), JSON.stringify({ ...payload, savedAt: Date.now() }));
  } catch {
    // Session cache is best-effort only.
  }
}

function avatarImageUrl(url) {
  if (!url) return '';
  const value = String(url);
  if (value.startsWith('/media/') || value.startsWith('/outfit-detective/') || value.includes('/outfit-detective/')) return value;
  return `/api/media/avatar?url=${encodeURIComponent(url)}`;
}

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function shortPostDescription(value) {
  const text = String(value || '').trim();
  if (text.length <= 25) return text;
  return `${text.slice(0, 22).trimEnd()}...`;
}

const Feed = () => {
  const { formatDate, language, t } = useI18n();
  const { isAuthenticated, requestLogin } = useAuth();
  const initialCacheRef = useRef(readFeedCache(language));
  const feedRandomSeedRef = useRef(readFeedRandomSeed());
  const [feed, setFeed] = useState(() => initialCacheRef.current?.feed || []);
  const [follows, setFollows] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [feedMode, setFeedMode] = useState(() => initialCacheRef.current?.feedMode || 'personal');
  const [loading, setLoading] = useState(() => !(initialCacheRef.current?.feed?.length));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreFeed, setHasMoreFeed] = useState(() => Boolean(initialCacheRef.current?.hasMoreFeed));
  const [feedOffset, setFeedOffset] = useState(() => Number(initialCacheRef.current?.feedOffset || 0));
  const [error, setError] = useState('');
  const [confirmUnfollow, setConfirmUnfollow] = useState(null);
  const [confirmingUnfollow, setConfirmingUnfollow] = useState(false);
  const [followSearch, setFollowSearch] = useState('');
  const [savedLookIds, setSavedLookIds] = useState(() => new Set(getSavedLooks().map((item) => String(item.id))));
  const feedColumnRef = useRef(null);
  const feedLoadMoreRef = useRef(null);
  useModalEscape(Boolean(confirmUnfollow), () => setConfirmUnfollow(null));

  const filteredFollows = useMemo(() => {
    const query = normalizeSearchText(followSearch);
    if (!query) return follows;
    return follows.filter((item) => normalizeSearchText([item.name, item.slug].filter(Boolean).join(' ')).includes(query));
  }, [followSearch, follows]);

  const loadFeedPage = useCallback(async (offset = 0, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const feedRes = await api.getFeed({ limit: FEED_PAGE_LIMIT, offset, seed: feedRandomSeedRef.current, language });
      const nextItems = feedRes.data || [];
      setFeedMode(feedRes.meta?.mode || 'personal');
      setFeed((current) => (append ? [...current, ...nextItems] : nextItems));
      setFeedOffset(offset + nextItems.length);
      setHasMoreFeed(nextItems.length === FEED_PAGE_LIMIT);
      return nextItems;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [language]);

  const loadShellData = useCallback(async () => {
    if (!isAuthenticated) {
      setFollows([]);
      setSuggestions([]);
      return;
    }

    const [followsRes, celebrityRes] = await Promise.all([
      api.getFollows(),
      api.getCelebrities(),
    ]);

    setFollows(followsRes.data || []);
    const followedIds = new Set((followsRes.data || []).map((item) => item.id));
    setSuggestions((celebrityRes.data || []).filter((item) => !followedIds.has(item.id)).slice(0, 6));
  }, [isAuthenticated]);

	  const load = useCallback(async () => {
	      try {
	        await Promise.all([
	          loadFeedPage(0, false),
	          loadShellData(),
	        ]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        initialCacheRef.current = null;
      }
  }, [loadFeedPage, loadShellData]);

  useEffect(() => {
    let mounted = true;

    async function loadMounted() {
      if (!mounted) return;
      await load();
      window.setTimeout(() => {
        const savedY = Number(sessionStorage.getItem(FEED_SCROLL_KEY) || 0);
        if (!Number.isFinite(savedY) || savedY <= 0) return;
        feedColumnRef.current?.scrollTo({ top: savedY, behavior: 'auto' });
        window.scrollTo({ top: savedY, behavior: 'auto' });
      }, 80);
    }

    loadMounted();

    return () => {
      mounted = false;
    };
  }, [load]);

  useEffect(() => {
    function saveFeedScroll() {
      const scrollY = feedColumnRef.current?.scrollTop || window.scrollY || 0;
      sessionStorage.setItem(FEED_SCROLL_KEY, String(Math.max(0, Math.round(scrollY))));
    }

    const feedNode = feedColumnRef.current;
    feedNode?.addEventListener('scroll', saveFeedScroll, { passive: true });
    window.addEventListener('scroll', saveFeedScroll, { passive: true });
    window.addEventListener('pagehide', saveFeedScroll);
    return () => {
      saveFeedScroll();
      feedNode?.removeEventListener('scroll', saveFeedScroll);
      window.removeEventListener('scroll', saveFeedScroll);
      window.removeEventListener('pagehide', saveFeedScroll);
    };
  }, []);

  useEffect(() => {
    if (loading || loadingMore) return;
    writeFeedCache(language, {
      feed,
      feedMode,
      hasMoreFeed,
      feedOffset,
    });
  }, [feed, feedMode, feedOffset, hasMoreFeed, language, loading, loadingMore]);

  useEffect(() => {
    function refreshFromLogo() {
      feedColumnRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      load();
    }

    window.addEventListener('od:feed-refresh', refreshFromLogo);
    return () => window.removeEventListener('od:feed-refresh', refreshFromLogo);
  }, [load]);

  useEffect(() => {
    function handleFeedPaging(event) {
      if (!['PageDown', 'PageUp', 'Home', 'End'].includes(event.key)) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target?.closest?.('input, textarea, select, button, a, [contenteditable="true"]')) return;

      const feedNode = feedColumnRef.current;
      const scrollableFeed = feedNode && feedNode.scrollHeight > feedNode.clientHeight + 8;
      const target = scrollableFeed ? feedNode : window;
      const viewport = scrollableFeed ? feedNode.clientHeight : window.innerHeight;
      const currentTop = scrollableFeed ? feedNode.scrollTop : window.scrollY;
      const maxTop = scrollableFeed
        ? feedNode.scrollHeight - feedNode.clientHeight
        : document.documentElement.scrollHeight - window.innerHeight;
      const pageStep = Math.max(320, Math.round(viewport * 0.86));
      let nextTop = currentTop;

      if (event.key === 'PageDown') nextTop = Math.min(maxTop, currentTop + pageStep);
      if (event.key === 'PageUp') nextTop = Math.max(0, currentTop - pageStep);
      if (event.key === 'Home') nextTop = 0;
      if (event.key === 'End') nextTop = maxTop;

      event.preventDefault();
      target.scrollTo({ top: nextTop, behavior: 'smooth' });
    }

    window.addEventListener('keydown', handleFeedPaging);
    return () => window.removeEventListener('keydown', handleFeedPaging);
  }, []);

  useEffect(() => {
    const node = feedLoadMoreRef.current;
    if (!node || !hasMoreFeed || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadFeedPage(feedOffset, true);
        }
      },
      {
        root: feedColumnRef.current,
        rootMargin: '260px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [feedOffset, hasMoreFeed, loadFeedPage, loading, loadingMore]);

  useEffect(() => {
    if (feedMode !== 'popular') return undefined;

    const timer = window.setInterval(async () => {
      try {
        const feedRes = await api.getFeed({ limit: FEED_PAGE_LIMIT, offset: 0, seed: feedRandomSeedRef.current });
        if (feedRes.meta?.mode === 'personal') {
          const nextItems = feedRes.data || [];
          setFeed(nextItems);
          setFeedMode('personal');
          setFeedOffset(nextItems.length);
          setHasMoreFeed(nextItems.length === FEED_PAGE_LIMIT);
        }
      } catch {
        // Discovery feed can stay visible while personal content is being prepared.
      }
    }, 15000);

    return () => window.clearInterval(timer);
  }, [feedMode]);

  useEffect(() => {
    function refreshSavedState() {
      setSavedLookIds(new Set(getSavedLooks().map((item) => String(item.id))));
    }

    window.addEventListener('od:saved-looks-change', refreshSavedState);
    window.addEventListener('storage', refreshSavedState);
    return () => {
      window.removeEventListener('od:saved-looks-change', refreshSavedState);
      window.removeEventListener('storage', refreshSavedState);
    };
  }, []);

  async function removeFollow(celebrityId) {
    try {
      await api.unfollowCelebrity(celebrityId);
      setFeed((current) => current.map((event) => (
        Number(event.celebrity_id) === Number(celebrityId) ? { ...event, is_following: 0 } : event
      )));
      await loadShellData();
    } catch (err) {
      setError(t('discover.removeError'));
    }
  }

  async function confirmRailUnfollow() {
    if (!confirmUnfollow?.id || confirmingUnfollow) return;
    setConfirmingUnfollow(true);
    try {
      await removeFollow(confirmUnfollow.id);
      setConfirmUnfollow(null);
    } finally {
      setConfirmingUnfollow(false);
    }
  }

  async function addFollow(celebrityId) {
    if (!isAuthenticated) {
      requestLogin({
        title: t('authPrompt.followTitle'),
        copy: t('authPrompt.followCopy'),
        afterLogin: () => addFollow(celebrityId),
      });
      return;
    }
    try {
      await api.followCelebrity(celebrityId);
      setFeed((current) => current.map((event) => (
        Number(event.celebrity_id) === Number(celebrityId) ? { ...event, is_following: 1 } : event
      )));
      await loadShellData();
    } catch (err) {
      if (err?.message === 'celebrity_banned' || err?.message === 'celebrity_not_found' || err?.payload?.error === 'celebrity_banned') {
        setSuggestions((current) => current.filter((item) => Number(item.id) !== Number(celebrityId)));
        setError(t('discover.followError'));
        return;
      }
      setError(t('discover.removeError'));
    }
  }

  async function toggleEventFollow(event) {
    if (!isAuthenticated) {
      requestLogin({
        title: t('authPrompt.followTitle'),
        copy: t('authPrompt.followCopy'),
        afterLogin: () => addFollow(event.celebrity_id),
      });
      return;
    }
    if (event.is_following) {
      await removeFollow(event.celebrity_id);
    } else {
      await addFollow(event.celebrity_id);
    }
  }

  async function shareEvent(event) {
    const url = new URL(`/events/${event.id}`, window.location.origin).href;
    const title = t('feed.shareTitle', { name: event.celebrity_name || 'Outfit Detective' });
    const text = t('feed.shareText', { title: event.title || t('feed.shareFallback') });
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch {
        // User cancelled or platform rejected the share payload.
      }
    }
    try {
      await navigator.clipboard.writeText([text, url].join(' '));
      setError('');
    } catch {
      setError(t('feed.shareFailed'));
    }
  }

  function toggleFeedSaved(event) {
    if (!isAuthenticated) {
      requestLogin({
        title: t('authPrompt.savedTitle'),
        copy: t('authPrompt.savedCopy'),
        afterLogin: () => toggleFeedSaved(event),
      });
      return;
    }
    const saved = toggleSavedLook(event);
    setSavedLookIds((current) => {
      const next = new Set(current);
      if (saved) next.add(String(event.id));
      else next.delete(String(event.id));
      return next;
    });
  }

  function matchedPiecesLabel(event) {
    const count = Number(event.matched_items_count || event.items_count || event.item_count || event.products_count || 0);
    if (count > 0) return t('feed.badgeMatched', { count });
    if (event.is_following) return t('feed.badgeFresh');
    return t('feed.badgePopular');
  }

  function sourceLabel(event) {
    return event.source_name || t('copyright.sourceFallback');
  }

  const dateOptions = {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  };

  return (
    <div className="ct-social-layout">
      <aside className="ct-left-rail">
        <div className="ct-panel-heading">
          <div>
            <p className="ct-kicker">{t('feed.following')}</p>
            <h2>{t('feed.yourList')}</h2>
          </div>
          <Link to="/discover" className="ct-icon-link" title={t('feed.findProfiles')}>
            <i className="ph ph-plus"></i>
          </Link>
        </div>

        {follows.length === 0 ? (
          <div className="ct-rail-empty">
            <p>{t('feed.noProfiles')}</p>
            <Link to="/discover" className="btn btn-dark btn-sm">
              {t('feed.findProfiles')}
            </Link>
          </div>
        ) : (
          <>
            <label className="ct-follow-search">
              <i className="ph ph-magnifying-glass"></i>
              <input
                type="search"
                value={followSearch}
                onChange={(event) => setFollowSearch(event.target.value)}
                placeholder={t('feed.searchList')}
                aria-label={t('feed.searchList')}
              />
              {followSearch ? (
                <button type="button" onClick={() => setFollowSearch('')} aria-label={t('common.cancel')}>
                  <i className="ph ph-x"></i>
                </button>
              ) : null}
            </label>
            {filteredFollows.length === 0 ? (
              <div className="ct-rail-empty compact">
                <p>{t('feed.noListSearchResults')}</p>
              </div>
            ) : (
              <div className="ct-follow-list">
                {filteredFollows.map((item) => (
                  <div className="ct-follow-row" key={item.id}>
                    <Link to={`/following/${item.slug}`} className="ct-avatar small">
                      {item.image_url ? <img src={avatarImageUrl(item.image_url)} alt={item.name} onError={(e) => { e.currentTarget.replaceWith(document.createTextNode((item.name || '?').slice(0, 1).toUpperCase())); }} /> : (item.name || '?').slice(0, 1).toUpperCase()}
                    </Link>
                    <div>
                      <Link to={`/following/${item.slug}`}>
                        <strong>{item.name}</strong>
                      </Link>
                    </div>
                    <button type="button" onClick={() => setConfirmUnfollow(item)} title={t('common.unfollow')}>
                      <i className="ph ph-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="ct-rail-brand-card" aria-label="Outfit Detective">
          <img src="/brand/outfit-detective-logo-transparent.svg" alt="Outfit Detective" />
        </div>
      </aside>

      {confirmUnfollow ? (
        <div className="ct-confirm-overlay" role="presentation" onMouseDown={() => setConfirmUnfollow(null)}>
          <section
            className="ct-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ct-unfollow-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button type="button" className="ct-confirm-close" onClick={() => setConfirmUnfollow(null)} aria-label={t('common.cancel')}>
              <i className="ph ph-x"></i>
            </button>
            <p className="ct-kicker">{t('common.unfollow')}</p>
            <h2 id="ct-unfollow-title">{t('followConfirm.title')}</h2>
            <p>{t('followConfirm.copy', { name: confirmUnfollow.name })}</p>
            <div className="ct-confirm-actions">
              <button type="button" className="secondary" onClick={() => setConfirmUnfollow(null)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="danger" onClick={confirmRailUnfollow} disabled={confirmingUnfollow}>
                {confirmingUnfollow ? t('followConfirm.confirming') : t('common.unfollow')}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <section className="ct-feed-column" ref={feedColumnRef}>
        {error ? <div className="alert alert-danger">{t('feed.apiError', { error })}</div> : null}
        {loading ? <div className="ct-card ct-empty">{t('feed.loading')}</div> : null}

        {!loading && isAuthenticated && feedMode === 'popular' && feed.length > 0 ? (
          <div className="ct-feed-mode-note">
            <i className="ph ph-sparkle"></i>
            <div>
              <strong>{t('feed.popularFallbackTitle')}</strong>
              <span>{follows.length > 0 ? t('feed.personalPendingCopy') : t('feed.popularFallbackCopy')}</span>
              <Link to="/discover" className="ct-feed-mode-link">
                {t('feed.popularFallbackCta')}
              </Link>
            </div>
          </div>
        ) : null}

        {!loading && !isAuthenticated ? (
          <article className="ct-post-card ct-public-intro-post">
            <div className="ct-post-head">
              <span className="ct-avatar">
                <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
              </span>
              <div>
                <strong>Outfit Detective</strong>
              </div>
              <button
                type="button"
                className="ct-post-follow-action"
                onClick={() => requestLogin({ title: t('authPrompt.title'), copy: t('authPrompt.copy') })}
              >
                {t('authPrompt.signIn')}
              </button>
            </div>

            <div className="ct-public-intro-media" aria-label={t('publicIntro.demoLabel')}>
              <Link to={`/t/${introDemoFit.shareId}`} className="ct-public-intro-demo-tile">
                <img src={introDemoFit.sourceImageUrl} alt={t('publicIntro.sourceAlt')} />
                <span>{t('publicIntro.mediaSource')}</span>
              </Link>
              <Link to={`/t/${introDemoFit.shareId}`} className="ct-public-intro-demo-tile result">
                <img src={introDemoFit.resultImageUrl} alt={t('publicIntro.resultAlt')} />
                <span>{t('publicIntro.mediaResult')}</span>
              </Link>
            </div>

            <div className="ct-post-body">
              <h1>{t('publicIntro.title')}</h1>
              <p className="ct-public-intro-how">{t('publicIntro.howCopy')}</p>
              <div className="ct-public-intro-actions">
                <button
                  type="button"
                  className="ct-ai-action"
                  onClick={() => requestLogin({
                    title: t('authPrompt.tryOnTitle'),
                    copy: t('authPrompt.tryOnCopy'),
                    afterLogin: () => {
                      window.location.href = `/events/${introDemoFit.eventId}?tryon=1`;
                    },
                  })}
                >
                  <i className="ph ph-magic-wand"></i>
                  <span>{t('publicIntro.primaryCta')}</span>
                </button>
                <Link to="/fit-wall" className="ct-public-intro-link">
                  {t('publicIntro.secondaryCta')}
                </Link>
              </div>
            </div>
          </article>
        ) : null}

        {!loading && feed.length === 0 ? (
          <div className="ct-card ct-empty">
            <h2>{t('feed.emptyTitle')}</h2>
            <p>{t('feed.emptyCopy')}</p>
            <Link to="/discover" className="btn btn-dark">
              {t('feed.findProfiles')}
            </Link>
          </div>
        ) : null}

        {feed.map((event, index) => (
          <article className="ct-post-card" key={event.id} id={index === 0 ? 'public-feed' : undefined}>
            <div className="ct-post-head">
              <Link to={`/following/${event.celebrity_slug}`} className="ct-avatar">
                {event.celebrity_image_url ? <img src={avatarImageUrl(event.celebrity_image_url)} alt={event.celebrity_name} onError={(e) => { e.currentTarget.replaceWith(document.createTextNode((event.celebrity_name || '?').slice(0, 1).toUpperCase())); }} /> : (event.celebrity_name || '?').slice(0, 1).toUpperCase()}
              </Link>
              <div>
                <Link to={`/following/${event.celebrity_slug}`}>
                  <strong>{event.celebrity_name}</strong>
                </Link>
                <span>{event.source_name} · {formatDate(event.event_at, dateOptions)}</span>
              </div>
              <button type="button" className="ct-post-follow-action" onClick={() => toggleEventFollow(event)}>
                {event.is_following ? t('common.unfollow') : t('common.follow')}
              </button>
            </div>
            <Link to={`/events/${event.id}`} className="ct-post-media">
              <img src={event.image_url || fallbackLooks[index % fallbackLooks.length]} alt={event.title} />
              <span className="ct-post-badge">{matchedPiecesLabel(event)}</span>
            </Link>
            <div className="ct-post-body">
              <div className="ct-post-actions">
                <Link
                  to={`/events/${event.id}`}
                  className="ct-feed-action-primary"
                  title={t('feed.aiAction')}
                  aria-label={t('feed.aiAction')}
                >
                  <i className="ph ph-sparkle"></i>
                  <span>{t('feed.aiAction')}</span>
                </Link>
                <Link
                  to={`/events/${event.id}?tryon=1`}
                  className="ct-feed-action-secondary"
                  title={t('feed.tryOn')}
                  onClick={(clickEvent) => {
                    if (isAuthenticated) return;
                    clickEvent.preventDefault();
                    requestLogin({
                      title: t('authPrompt.tryOnTitle'),
                      copy: t('authPrompt.tryOnCopy'),
                      afterLogin: () => {
                        window.location.href = `/events/${event.id}?tryon=1`;
                      },
                    });
                  }}
                >
                  <i className="ph ph-magic-wand"></i>
                  <span>{t('feed.tryOn')}</span>
                </Link>
                <button
                  type="button"
                  className={`ct-feed-icon-action${savedLookIds.has(String(event.id)) || isLookSaved(event.id) ? ' active' : ''}`}
                  onClick={() => toggleFeedSaved(event)}
                  title={savedLookIds.has(String(event.id)) ? t('feed.unsave') : t('feed.save')}
                  aria-label={savedLookIds.has(String(event.id)) ? t('feed.unsave') : t('feed.save')}
                >
                  <i className={savedLookIds.has(String(event.id)) ? 'ph-fill ph-bookmark-simple' : 'ph ph-bookmark-simple'}></i>
                </button>
                <button type="button" className="ct-feed-icon-action" onClick={() => shareEvent(event)} title={t('feed.share')} aria-label={t('feed.share')}>
                  <i className="ph ph-paper-plane-tilt"></i>
                </button>
              </div>
              <h2 className="ct-feed-title-short" title={event.title}>{shortDisplayTitle(event.title)}</h2>
              {event.description ? (
                <p className="ct-post-description" title={event.description}>
                  {shortPostDescription(event.description)}
                </p>
              ) : null}
              <div className="ct-content-credit">
                <span>{t('copyright.source', { source: sourceLabel(event) })}</span>
                <small>{t('copyright.preview')}</small>
                <Link to="/contact">{t('copyright.takedown')}</Link>
              </div>
              <Link to={`/events/${event.id}`} className="ct-text-link">
                {t('feed.openDetail')}
              </Link>
            </div>
          </article>
        ))}
        {!loading && feed.length > 0 && (hasMoreFeed || loadingMore) ? (
          <div className={`ct-feed-load-more ${loadingMore ? 'active' : 'pending'}`} ref={feedLoadMoreRef}>
            {loadingMore || hasMoreFeed ? (
              <>
                <span className="ct-input-spinner"></span>
                <span>{t('discover.loadingMore')}</span>
              </>
            ) : null}
          </div>
        ) : null}
      </section>

      <aside className="ct-right-rail">
        <BetaInviteCard compact />
        <div className="ct-panel-heading">
          <div>
            <p className="ct-kicker">{t('app.discover')}</p>
            <h2>{t('feed.suggested')}</h2>
          </div>
        </div>
        <div className="ct-follow-list">
          {suggestions.length === 0 ? (
            <p className="ct-muted">{t('feed.noSuggestions')}</p>
          ) : (
            suggestions.map((item) => (
              <div className="ct-follow-row suggestion" key={item.id}>
                <Link to={`/following/${item.slug}`} className="ct-avatar small">
                  {item.image_url ? <img src={avatarImageUrl(item.image_url)} alt={item.name} onError={(e) => { e.currentTarget.replaceWith(document.createTextNode((item.name || '?').slice(0, 1).toUpperCase())); }} /> : (item.name || '?').slice(0, 1).toUpperCase()}
                </Link>
                <div>
                  <Link to={`/following/${item.slug}`}>
                    <strong>{item.name}</strong>
                  </Link>
                </div>
                <button type="button" onClick={() => addFollow(item.id)}>
                  {t('common.follow')}
                </button>
              </div>
            ))
          )}
        </div>

      </aside>
    </div>
  );
};

export default Feed;
